import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Gamepad2, MessagesSquare, Sparkles } from "lucide-react";

import { cn } from "lib/utils";

/* -------------------------------------------------------------------------- */
/*  Fruit-slice mini-game (lightweight canvas, no extra deps)                 */
/* -------------------------------------------------------------------------- */

const FRUITS = ["🍉", "🍎", "🍊", "🍋", "🍓", "🍇", "🥝", "🍍"];
const GAME_SECONDS = 30;

interface Fruit {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  emoji: string;
  sliced: boolean;
  slicedAt: number;
}

const FruitGame = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [playing, setPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [best, setBest] = useState(() =>
    Number(localStorage.getItem("home-game-best") || 0)
  );

  // Mutable game state kept in refs so the rAF loop stays stable.
  const fruitsRef = useRef<Fruit[]>([]);
  const rafRef = useRef<number>();
  const lastSpawnRef = useRef(0);
  const playingRef = useRef(false);
  const scoreRef = useRef(0);
  const pointerDownRef = useRef(false);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = undefined;
  }, []);

  const endGame = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
    stopLoop();
    setBest((prev) => {
      const next = Math.max(prev, scoreRef.current);
      localStorage.setItem("home-game-best", String(next));
      return next;
    });
  }, [stopLoop]);

  const loop = useCallback(
    (now: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;

      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      // Spawn a new fruit roughly every 650ms.
      if (now - lastSpawnRef.current > 650) {
        lastSpawnRef.current = now;
        const x = 40 + Math.random() * (width - 80);
        fruitsRef.current.push({
          id: now + Math.random(),
          x,
          y: height + 30,
          vx: (Math.random() - 0.5) * 2.4,
          vy: -(9 + Math.random() * 4),
          emoji: FRUITS[Math.floor(Math.random() * FRUITS.length)],
          sliced: false,
          slicedAt: 0,
        });
      }

      const gravity = 0.18;
      fruitsRef.current.forEach((f) => {
        if (!f.sliced) {
          f.x += f.vx;
          f.y += f.vy;
          f.vy += gravity;
        }
        ctx.save();
        ctx.font = "44px serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        if (f.sliced) {
          const t = Math.min(1, (now - f.slicedAt) / 300);
          ctx.globalAlpha = 1 - t;
          ctx.translate(f.x, f.y);
          ctx.scale(1 + t * 0.6, 1 + t * 0.6);
          ctx.fillText(f.emoji, 0, 0);
        } else {
          ctx.fillText(f.emoji, f.x, f.y);
        }
        ctx.restore();
      });

      // Drop fruits that fell off-screen or finished their slice animation.
      fruitsRef.current = fruitsRef.current.filter((f) => {
        if (f.sliced) return now - f.slicedAt < 300;
        return f.y < height + 80;
      });

      rafRef.current = requestAnimationFrame(loop);
    },
    []
  );

  const startGame = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (canvas && wrap) {
      canvas.width = wrap.clientWidth;
      canvas.height = wrap.clientHeight;
    }
    fruitsRef.current = [];
    scoreRef.current = 0;
    setScore(0);
    setTimeLeft(GAME_SECONDS);
    playingRef.current = true;
    setPlaying(true);
    lastSpawnRef.current = 0;
    stopLoop();
    rafRef.current = requestAnimationFrame(loop);
  }, [loop, stopLoop]);

  // Countdown timer.
  useEffect(() => {
    if (!playing) return;
    if (timeLeft <= 0) {
      endGame();
      return;
    }
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [playing, timeLeft, endGame]);

  useEffect(() => () => stopLoop(), [stopLoop]);

  const sliceAt = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !playingRef.current) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    fruitsRef.current.forEach((f) => {
      if (f.sliced) return;
      if (Math.hypot(f.x - x, f.y - y) < 34) {
        f.sliced = true;
        f.slicedAt = performance.now();
        scoreRef.current += 1;
        setScore(scoreRef.current);
      }
    });
  }, []);

  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/60 shadow-soft-lg backdrop-blur">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Gamepad2 className="h-4 w-4 text-primary" />
          Fruit Slice
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground">
            Score <b className="text-foreground">{score}</b>
          </span>
          <span className="text-muted-foreground">
            Best <b className="text-foreground">{best}</b>
          </span>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 font-medium",
              timeLeft <= 5 && playing
                ? "bg-destructive/10 text-destructive"
                : "bg-primary/10 text-primary"
            )}
          >
            {timeLeft}s
          </span>
        </div>
      </div>

      <div
        ref={wrapRef}
        className="relative h-72 w-full bg-gradient-to-b from-primary/5 to-accent/10"
      >
        <canvas
          ref={canvasRef}
          className="h-full w-full touch-none"
          onPointerDown={(e) => {
            pointerDownRef.current = true;
            sliceAt(e.clientX, e.clientY);
          }}
          onPointerUp={() => (pointerDownRef.current = false)}
          onPointerLeave={() => (pointerDownRef.current = false)}
          onPointerMove={(e) => {
            if (pointerDownRef.current) sliceAt(e.clientX, e.clientY);
          }}
        />

        {!playing && (
          <div className="absolute inset-0 grid place-items-center bg-background/40 backdrop-blur-sm">
            <button
              type="button"
              onClick={startGame}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow transition-transform hover:scale-105"
            >
              <Gamepad2 className="h-4 w-4" />
              {timeLeft === 0 ? "Play again" : "Start game"}
            </button>
          </div>
        )}
      </div>
      <p className="px-4 py-2 text-center text-xs text-muted-foreground">
        Click or drag across the fruit to slice it before time runs out!
      </p>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Home page                                                                 */
/* -------------------------------------------------------------------------- */

const Home = () => {
  document.title = "Home | EngageHub IQ";
  const navigate = useNavigate();

  // 3D tilt for the hero card based on pointer position.
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const handleTilt = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ rx: -py * 12, ry: px * 12 });
  };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8">
      <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
        {/* Animated gradient + floating orbs background */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-accent/10 to-background bg-[length:200%_200%] animate-gradient"
        />
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <motion.div
            className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-primary/20 blur-3xl"
            animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-0 right-10 h-80 w-80 rounded-full bg-accent/20 blur-3xl"
            animate={{ y: [0, -25, 0], x: [0, -15, 0] }}
            transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-12 lg:grid-cols-2 lg:items-center lg:py-20">
          {/* Left: hero copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Welcome to EngageHub IQ
            </span>
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl">
              Connect, chat, and{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                stay in sync
              </span>{" "}
              with your team.
            </h1>
            <p className="max-w-md text-base text-muted-foreground">
              A vibrant personal messaging space. Jump into a conversation, or
              take a quick break with a casual game.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/communication")}
                className="group inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition-transform hover:scale-105"
              >
                <MessagesSquare className="h-4 w-4" />
                Open Communication
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/friends")}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card/60 px-6 py-3 text-sm font-semibold text-foreground backdrop-blur transition-colors hover:bg-muted/60"
              >
                Find Friends
              </button>
            </div>
          </motion.div>

          {/* Right: 3D tilt card + mini-game */}
          <div className="space-y-6" style={{ perspective: "1200px" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              onMouseMove={handleTilt}
              onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
              style={{
                transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
                transformStyle: "preserve-3d",
                transition: "transform 0.1s ease-out",
              }}
              className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary to-accent p-8 text-primary-foreground shadow-soft-lg"
            >
              <div
                aria-hidden
                className="absolute inset-0 opacity-30 mix-blend-overlay"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)",
                  backgroundSize: "26px 26px",
                }}
              />
              <div
                style={{ transform: "translateZ(40px)" }}
                className="relative space-y-3"
              >
                <Sparkles className="h-8 w-8" />
                <h3 className="text-2xl font-bold">EngageHub IQ</h3>
                <p className="text-sm text-white/80">
                  Real-time messaging, presence, and a little bit of fun — all
                  in one place.
                </p>
              </div>
            </motion.div>

            <FruitGame />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
